/**
 * Push Notification Service - Firebase Cloud Messaging Integration
 * 
 * Handles push notification registration and delivery for Android/iOS.
 * 
 * Usage:
 *   import { initializePushNotifications } from './services/pushNotificationService';
 *   
 *   // On app startup
 *   await initializePushNotifications(userEmail, backendUrl);
 * 
 * Features:
 * - Requests notification permission
 * - Registers device token with backend
 * - Handles incoming push notifications
 * - Deep links to submissions when tapped
 * 
 * Phase 3 Integration
 */

import { registerUserDeviceToken, unregisterUserDeviceToken } from './apiService';

// Conditional imports - only load Capacitor in native builds
let PushNotifications, Capacitor;
try {
  // Try to import Capacitor modules (will fail in web build, that's OK)
  PushNotifications = require('@capacitor/push-notifications').PushNotifications;
  Capacitor = require('@capacitor/core').Capacitor;
} catch (e) {
  // Web build - Capacitor not available
  console.log('📱 Push notifications not available in web build (Capacitor modules not loaded)');
}

/**
 * Initialize push notifications for the current user.
 * Call this after user logs in.
 */
export const initializePushNotifications = async (userEmail, backendUrl) => {
  // Push notifications disabled — Firebase not configured yet (Phase 3 future)
  // Re-enable after adding google-services.json and POST_NOTIFICATIONS permission
  return { success: false, reason: 'disabled' };
};

/**
 * Setup push notification listeners.
 * Call this once during app initialization.
 */
export const setupPushListeners = (backendUrl, navigate, refreshInboxCount) => {
  // Push notifications disabled — Firebase not configured yet (Phase 3 future)
  return;
};

/**
 * Unregister from push notifications.
 * Call this when user logs out or disables notifications.
 */
export const disablePushNotifications = async (userEmail, backendUrl) => {
  try {
    if (!Capacitor || !PushNotifications || !Capacitor.isNativePlatform()) {
      return { success: false, reason: 'web_platform' };
    }
    
    console.log('🔕 Disabling push notifications...');
    
    // 1. Unregister from backend
    if (userEmail) {
      await unregisterUserDeviceToken(userEmail);
    }
    
    // 2. Remove all listeners (optional - they'll be removed on app close anyway)
    await PushNotifications.removeAllListeners();
    
    console.log('✅ Push notifications disabled');
    return { success: true };
    
  } catch (error) {
    console.error('❌ Error disabling push:', error);
    return { success: false, reason: error.message };
  }
};

/**
 * Check if push notifications are available and enabled.
 */
export const checkPushStatus = async () => {
  try {
    if (!Capacitor || !PushNotifications || !Capacitor.isNativePlatform()) {
      return { available: false, granted: false, reason: 'web_platform' };
    }
    
    const permStatus = await PushNotifications.checkPermissions();
    
    return {
      available: true,
      granted: permStatus.receive === 'granted',
      status: permStatus.receive
    };
    
  } catch (error) {
    console.error('❌ Error checking push status:', error);
    return { available: false, granted: false, reason: error.message };
  }
};
